// outpost-crm-ui/src/ChairsMap.jsx
// Google Maps overlay for the Chairs tab — UGA-better version.
// - Custom SVG markers, color-coded by status
// - Building-level clustering (chairs sharing lat/lng round to 4 decimals)
// - InfoWindow on click with chair details + click-through to detail modal
// - Filter strip (status, customer)
// - "Unlocated chairs" panel for chairs without lat/lng
//
// API key: reads process.env.REACT_APP_GOOGLE_MAPS_API_KEY at build time.
// Drew sets that on Vercel (same value as GOOGLE_MAPS_API_KEY on Railway).

import { useState, useEffect, useRef } from "react";
import { loadGoogleMaps } from "./googleMapsLoader";

const C = {
  green:    "#494C43",
  orange:   "#D66F27",
  white:    "#FFFFFF",
  cream:    "#FAF9F5",
  border:   "#E5E5E0",
  light:    "#F0EFEA",
  grayMid:  "#9A9B96",
  grayDark: "#5C5E57",
  success:  "#4CAF50",
  warning:  "#FFC107",
  danger:   "#F44336",
};

// Status → color for marker icon
const STATUS_COLOR = {
  in_place:    C.success,
  moved:       C.orange,
  missing:     C.danger,
  maintenance: C.warning,
  inventory:   C.grayMid,
  retired:     "#777777",
};

// SVG chair-icon marker. Returns a data URL Google Maps can use as Icon.
function chairMarkerSVG(color, count = 1) {
  // Pin shape with chair glyph + optional count badge
  const badge = count > 1
    ? `<circle cx="34" cy="6" r="9" fill="${C.green}" stroke="${C.white}" stroke-width="1.5"/>
       <text x="34" y="10" text-anchor="middle" font-family="Arial" font-size="10" font-weight="700" fill="${C.white}">${count}</text>`
    : "";
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52">
  <path d="M22 0 C10 0 2 8 2 20 C2 32 22 50 22 50 C22 50 42 32 42 20 C42 8 34 0 22 0 Z"
        fill="${color}" stroke="${C.white}" stroke-width="2"/>
  <rect x="14" y="13" width="16" height="3" rx="1" fill="${C.white}"/>
  <rect x="14" y="18" width="16" height="9" rx="1" fill="${C.white}"/>
  <rect x="14" y="29" width="3" height="6" fill="${C.white}"/>
  <rect x="27" y="29" width="3" height="6" fill="${C.white}"/>
  ${badge}
</svg>`.trim();
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Maps loader lives in ./googleMapsLoader so Chairs.jsx can share it
// (Places Autocomplete needs the same script tag).

// Group chairs by lat/lng (rounded to 4 decimals ≈ 11m tolerance) — chairs
// at the same building cluster into a single marker with a count badge.
function clusterChairs(chairs) {
  const buckets = new Map();
  for (const c of chairs) {
    if (c.location_lat == null || c.location_lng == null) continue;
    const key = `${(+c.location_lat).toFixed(4)},${(+c.location_lng).toFixed(4)}`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        lat: +c.location_lat,
        lng: +c.location_lng,
        chairs: [],
      });
    }
    buckets.get(key).chairs.push(c);
  }
  return Array.from(buckets.values());
}

// HTML for the InfoWindow content (a single building's chairs)
function infoWindowHTML(cluster) {
  const orgName = cluster.chairs[0].org_name || "(internal inventory)";
  const building = cluster.chairs[0].building_name || "Unknown building";
  const headerColor = C.green;

  const rows = cluster.chairs.map((c) => {
    const color = STATUS_COLOR[c.status] || C.grayMid;
    const status = (c.status || "unknown").replace("_", " ");
    const loc = [c.floor, c.stairwell_or_room].filter(Boolean).join(" / ") || "no location";
    const lastVerified = c.last_verified_date || "—";
    return `
      <div style="padding:6px 0; border-bottom: 1px solid ${C.light};">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
          <div style="font-weight: 600; color: ${C.green}; font-size: 13px;">
            ${c.halo_serial || "(no serial)"} · ${(c.model || "").toUpperCase()}
          </div>
          <span style="padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 600;
                       background: ${color}; color: ${C.white};">${status}</span>
        </div>
        <div style="color: ${C.grayDark}; font-size: 11px; margin-top: 2px;">
          ${loc} · last verified ${lastVerified}
        </div>
      </div>`;
  }).join("");

  return `
<div style="font-family: -apple-system, system-ui, sans-serif; min-width: 260px; max-width: 320px;">
  <div style="font-size: 10px; color: ${C.orange}; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">
    ${cluster.chairs.length} chair${cluster.chairs.length > 1 ? "s" : ""}
  </div>
  <div style="font-size: 15px; color: ${headerColor}; font-weight: 700; margin: 2px 0 1px;">${building}</div>
  <div style="font-size: 11px; color: ${C.grayDark}; margin-bottom: 8px;">${orgName}</div>
  ${rows}
</div>`;
}

export default function ChairsMap({ chairs, onChairClick }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [filter, setFilter] = useState({ status: "" });

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Apply filter
  const filteredChairs = chairs.filter((c) => {
    if (filter.status && c.status !== filter.status) return false;
    return true;
  });
  const located = filteredChairs.filter((c) => c.location_lat != null && c.location_lng != null);
  const unlocated = filteredChairs.filter((c) => c.location_lat == null || c.location_lng == null);

  const clusters = clusterChairs(located);

  // Initialize map once on mount
  useEffect(() => {
    if (!apiKey) {
      setStatus("nokey");
      return;
    }
    loadGoogleMaps()
      .then(() => {
        if (!mapDivRef.current) return;
        // Default center: continental US
        const map = new window.google.maps.Map(mapDivRef.current, {
          center: { lat: 36.7783, lng: -119.4179 }, // CA centroid
          zoom: 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
        mapRef.current = map;
        infoWindowRef.current = new window.google.maps.InfoWindow();
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [apiKey]);

  // Render markers when clusters change
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    // Clear old markers
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (!clusters.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    for (const cluster of clusters) {
      // Pick the worst status in the cluster to color the marker
      // (missing > maintenance > moved > in_place > inventory > retired)
      const PRIORITY = ["missing", "maintenance", "moved", "in_place", "inventory", "retired"];
      const worst = PRIORITY.find((s) => cluster.chairs.some((c) => c.status === s)) || "in_place";
      const color = STATUS_COLOR[worst] || C.grayMid;

      const marker = new window.google.maps.Marker({
        position: { lat: cluster.lat, lng: cluster.lng },
        map: mapRef.current,
        icon: {
          url: chairMarkerSVG(color, cluster.chairs.length),
          scaledSize: new window.google.maps.Size(44, 52),
          anchor: new window.google.maps.Point(22, 50),
        },
        title: `${cluster.chairs.length} chair${cluster.chairs.length > 1 ? "s" : ""} · ${cluster.chairs[0].building_name || ""}`,
      });
      // Hover: open InfoWindow on mouseover, close on mouseout.
      marker.addListener("mouseover", () => {
        infoWindowRef.current.setContent(infoWindowHTML(cluster));
        infoWindowRef.current.open(mapRef.current, marker);
      });
      marker.addListener("mouseout", () => {
        infoWindowRef.current.close();
      });
      // Click: open the chair detail modal if it's a single chair, else
      // keep the InfoWindow open (don't auto-close on next mouseout).
      marker.addListener("click", () => {
        if (cluster.chairs.length === 1 && onChairClick) {
          onChairClick(cluster.chairs[0]);
        } else {
          infoWindowRef.current.setContent(infoWindowHTML(cluster));
          infoWindowRef.current.open(mapRef.current, marker);
        }
      });
      markersRef.current.push(marker);
      bounds.extend({ lat: cluster.lat, lng: cluster.lng });
    }
    if (clusters.length === 1) {
      mapRef.current.setCenter({ lat: clusters[0].lat, lng: clusters[0].lng });
      mapRef.current.setZoom(15);
    } else if (clusters.length > 1) {
      mapRef.current.fitBounds(bounds, 60);
    }
  }, [status, clusters, onChairClick]);

  if (status === "nokey") {
    return (
      <div style={{ padding: 24, background: "#FFF5F5", borderRadius: 8, border: `1px solid ${C.danger}`, color: C.danger }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>⚠️ Map unavailable</div>
        <div style={{ fontSize: 13, color: C.grayDark }}>
          Set the <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> environment variable in Vercel
          (Settings → Environment Variables) and redeploy. Use the same value as your
          Railway <code>GOOGLE_MAPS_API_KEY</code>.
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={{ padding: 24, background: "#FFF5F5", borderRadius: 8, border: `1px solid ${C.danger}`, color: C.danger }}>
        Failed to load Google Maps. Check API key restrictions and that the Maps
        JavaScript API is enabled in Google Cloud Console.
      </div>
    );
  }

  const STATUSES = ["in_place", "moved", "missing", "maintenance", "inventory", "retired"];
  const inp = { padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 13, background: C.white };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <select value={filter.status} onChange={(e) => setFilter({ status: e.target.value })} style={inp}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <div style={{ fontSize: 12, color: C.grayMid }}>
          {located.length} chair{located.length === 1 ? "" : "s"} on map
          {clusters.length !== located.length && ` · ${clusters.length} location${clusters.length === 1 ? "" : "s"}`}
          {unlocated.length > 0 && ` · ${unlocated.length} not yet located`}
        </div>
        <div style={{ fontSize: 11, color: C.grayMid, marginLeft: 8, fontStyle: "italic" }}>
          hover a pin to see chair details
        </div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: C.grayMid, display: "flex", gap: 12 }}>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.success, marginRight: 4 }} />in place</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.orange, marginRight: 4 }} />moved</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.danger, marginRight: 4 }} />missing</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.warning, marginRight: 4 }} />maintenance</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: C.grayMid, marginRight: 4 }} />inventory</span>
        </div>
      </div>
      <div ref={mapDivRef} style={{ height: "640px", width: "100%", borderRadius: 8, border: `1px solid ${C.border}`, background: C.cream }} />
      {status === "loading" && (
        <div style={{ padding: 12, textAlign: "center", color: C.grayMid, fontSize: 13 }}>Loading map...</div>
      )}
      {unlocated.length > 0 && (
        <div style={{ marginTop: 12, padding: 12, background: C.cream, border: `1px solid ${C.border}`, borderRadius: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.orange, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            {unlocated.length} chair{unlocated.length === 1 ? "" : "s"} not yet located on the map
          </div>
          <div style={{ fontSize: 12, color: C.grayDark, marginBottom: 8 }}>
            Click a chair to add latitude/longitude. Without coordinates, chairs only show in the list view.
          </div>
          <div style={{ maxHeight: 180, overflowY: "auto" }}>
            {unlocated.map((c) => (
              <div key={c.id}
                onClick={() => onChairClick && onChairClick(c)}
                style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.light}`, cursor: "pointer", fontSize: 12 }}>
                <span>
                  <strong>{c.halo_serial || "(no serial)"}</strong> · {(c.model || "").toUpperCase()} · {c.org_name || "inventory"}
                </span>
                <span style={{ color: C.grayMid }}>{[c.building_name, c.floor].filter(Boolean).join(" / ") || "no location"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
